import { useEffect } from "react";
import { NEWS, CHANNELS, type NewsItem } from "../../../mocks/newsData";

function channelLabel(channel: string) {
  const found = CHANNELS.find((c) => c.id === channel);
  return found ? found.label : "综合";
}

type Props = {
  article: NewsItem;
  onClose: () => void;
  onOpen: (id: string) => void;
};

export default function ArticleModal({ article, onClose, onOpen }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const related = NEWS.filter((n) => n.channel === article.channel && n.id !== article.id).slice(0, 4);
  const relatedFallback =
    related.length > 0 ? related : NEWS.filter((n) => n.id !== article.id).slice(0, 4);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-foreground-950/60 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="文章详情">
      <div className="flex min-h-full items-start justify-center p-3 py-6 md:py-10">
        <article className="animate-riseIn w-full max-w-3xl overflow-hidden rounded-lg bg-background-50">
          <div className="relative h-44 md:h-64">
            <img
              src={article.cover}
              alt={article.title}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" aria-hidden="true" />
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-background-950/70 text-background-50 transition-colors hover:bg-background-950"
              aria-label="关闭文章"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
            <div className="absolute bottom-3 left-4 flex items-center gap-2 text-background-50 md:left-6">
              <span className="rounded-sm bg-primary-500 px-2 py-0.5 text-[11px] font-bold">
                {article.badge}
              </span>
              <span className="rounded-sm bg-background-950/60 px-2 py-0.5 text-[11px] font-medium">
                {channelLabel(article.channel)}
              </span>
            </div>
          </div>

          <div className="px-4 py-5 md:px-8 md:py-7">
            <h2 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
              {article.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line" aria-hidden="true" /> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true" /> {article.timeText}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true" /> {article.views.toLocaleString("zh-CN")} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line" aria-hidden="true" /> 约 {article.readMin} 分钟
              </span>
            </div>

            <p className="mt-4 border-l-2 border-primary-400 bg-primary-50 py-2 pl-4 text-sm leading-7 text-foreground-700">
              {article.excerpt}
            </p>

            <div className="mt-4 space-y-4 text-[15px] leading-8 text-foreground-800">
              {article.content.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2 border-t border-background-200 pt-5">
              {article.keywords.map((kw) => (
                <span
                  key={kw}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  # {kw}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* 相关阅读 */}
        <aside className="mt-6 w-full max-w-3xl">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-background-50">
            <span className="flex h-6 w-1 rounded-full bg-accent-400" aria-hidden="true" />
            相关阅读
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {relatedFallback.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onOpen(n.id)}
                className="group flex cursor-pointer items-start gap-3 rounded-md bg-background-100/90 p-3 text-left transition-colors hover:bg-background-100"
              >
                <img
                  src={n.cover}
                  alt=""
                  className="h-16 w-20 shrink-0 rounded-sm object-cover"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                    {n.title}
                  </span>
                  <span className="mt-auto text-[11px] text-foreground-500">{n.timeText}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded-md bg-background-200/80 px-5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
            >
              返回资讯列表
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}